import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useCallback,
  memo,
} from "react";
import SignaturePad from "signature_pad";

const SignatureField = memo(
  forwardRef(
    (
      {
        value = "",
        onChange,
        penColor = "black",
        backgroundColor = "white",
        disabled = false,
        label = "Digital Signature",
        required = false,
        maxSize = 100000,
        canvasProps = {},
        className = "",
      },
      ref
    ) => {
      const canvasRef = useRef(null);
      const padRef = useRef(null);
      const containerRef = useRef(null);
      const [isEmpty, setIsEmpty] = useState(!value);
      const [error, setError] = useState("");
      const [isInitialized, setIsInitialized] = useState(false);
      const mountedRef = useRef(true);

      // Track component mounted state
      useEffect(() => {
        mountedRef.current = true;
        return () => {
          mountedRef.current = false;
        };
      }, []);

      // Initialize or reinitialize the signature pad
      const initializePad = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;

        if (!canvas || !container || !mountedRef.current) {
          return;
        }

        // Clear any existing pad
        if (padRef.current) {
          try {
            padRef.current.off();
          } catch (e) {
            // Ignore errors when turning off
          }
        }

        // Function to resize canvas
        const resizeCanvas = () => {
          if (!canvas || !container || !mountedRef.current) return;

          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          const width = container.offsetWidth || 400;
          const height = 150;

          canvas.width = width * ratio;
          canvas.height = height * ratio;
          canvas.style.width = `${width}px`;
          canvas.style.height = `${height}px`;

          const ctx = canvas.getContext("2d");
          ctx.scale(ratio, ratio);
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, width, height);
        };

        // Resize canvas
        resizeCanvas();

        // Create new SignaturePad instance
        const pad = new SignaturePad(canvas, {
          minWidth: 0.5,
          maxWidth: 2.5,
          penColor: penColor,
          backgroundColor: backgroundColor,
          throttle: 16,
          velocityFilterWeight: 0.7,
        });

        padRef.current = pad;

        // Event handlers
        const handleStrokeEnd = () => {
          if (!mountedRef.current || !padRef.current) return;

          setTimeout(() => {
            if (!mountedRef.current || !padRef.current) return;

            const padIsEmpty = padRef.current.isEmpty();
            const dataURL = !padIsEmpty ? padRef.current.toDataURL() : "";

            setIsEmpty(padIsEmpty);

            if (!padIsEmpty && onChange) {
              onChange(dataURL);
            }
          }, 50);
        };

        const handleBeginStroke = () => {
          if (!mountedRef.current) return;
          setError("");
        };

        // Add event listeners
        pad.addEventListener("endStroke", handleStrokeEnd);
        pad.addEventListener("beginStroke", handleBeginStroke);

        // Enable/disable
        if (disabled) {
          pad.off();
        } else {
          pad.on();
        }

        // Load initial value if provided
        if (value && !pad.isEmpty()) {
          try {
            pad.fromDataURL(value);
            setIsEmpty(false);
          } catch (err) {
            // Silently handle error loading initial signature
          }
        }

        setIsInitialized(true);

        // Cleanup function
        return () => {
          if (pad) {
            pad.removeEventListener("endStroke", handleStrokeEnd);
            pad.removeEventListener("beginStroke", handleBeginStroke);
            pad.off();
          }
        };
      }, [penColor, backgroundColor, disabled, value, onChange]);

      // Initialize on mount and when container becomes available
      useEffect(() => {
        if (!isInitialized && canvasRef.current && containerRef.current) {
          const cleanup = initializePad();
          return cleanup;
        }
      }, [isInitialized, initializePad]);

      // Reinitialize if canvas ref changes (e.g., after re-render)
      useEffect(() => {
        const checkAndInit = () => {
          if (canvasRef.current && containerRef.current && !padRef.current) {
            initializePad();
          }
        };

        // Check immediately
        checkAndInit();

        // Also check after a short delay in case DOM isn't ready
        const timer = setTimeout(checkAndInit, 100);

        return () => clearTimeout(timer);
      }, [initializePad]);

      // Load value when it changes
      useEffect(() => {
        if (value && padRef.current && isInitialized) {
          try {
            padRef.current.fromDataURL(value);
            setIsEmpty(false);
          } catch (err) {
            // Silently handle error loading signature
          }
        }
      }, [value, isInitialized]);

      // Update pen color when it changes
      useEffect(() => {
        if (padRef.current && isInitialized) {
          padRef.current.penColor = penColor;
        }
      }, [penColor, isInitialized]);

      // Update disabled state
      useEffect(() => {
        if (padRef.current && isInitialized) {
          if (disabled) {
            padRef.current.off();
          } else {
            padRef.current.on();
          }
        }
      }, [disabled, isInitialized]);

      // Clear signature function
      const clearSignature = useCallback(() => {
        if (padRef.current) {
          padRef.current.clear();

          // Clear the canvas background
          const canvas = canvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }

          setIsEmpty(true);
          setError("");
          if (onChange) {
            onChange("");
          }
        }
      }, [onChange, backgroundColor]);

      // Get current signature
      const getCurrentSignature = useCallback(() => {
        if (padRef.current && !padRef.current.isEmpty()) {
          return padRef.current.toDataURL();
        }
        return value || "";
      }, [value]);

      // Manual capture
      const captureSignature = useCallback(() => {
        if (padRef.current && !padRef.current.isEmpty()) {
          const dataURL = padRef.current.toDataURL();
          if (onChange) {
            onChange(dataURL);
          }
          return dataURL;
        }
        return "";
      }, [onChange]);

      // Expose methods via ref
      useImperativeHandle(
        ref,
        () => ({
          clear: clearSignature,
          isEmpty: () => isEmpty,
          toDataURL: getCurrentSignature,
          getCurrentSignature: getCurrentSignature,
          getPad: () => padRef.current,
          capture: captureSignature,
          reinitialize: initializePad,
        }),
        [
          clearSignature,
          isEmpty,
          getCurrentSignature,
          captureSignature,
          initializePad,
        ]
      );

      return (
        <div className={`signature-field-container ${className}`}>
          {label && (
            <label className="form-label">
              {label}
              {required && <span className="text-danger ms-1">*</span>}
            </label>
          )}

          <div
            ref={containerRef}
            className="signature-pad-wrapper"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "500px",
              height: "150px",
              border: error ? "2px solid #dc3545" : "2px solid #dee2e6",
              borderRadius: "0.375rem",
              backgroundColor: backgroundColor,
              overflow: "hidden",
            }}
          >
            <canvas
              ref={canvasRef}
              className="signature-canvas"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                touchAction: "none",
                cursor: disabled ? "not-allowed" : "crosshair",
              }}
              {...canvasProps}
            />

            {disabled && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(248, 249, 250, 0.8)",
                  cursor: "not-allowed",
                }}
              >
                <span className="text-muted">
                  Signature editing is disabled
                </span>
              </div>
            )}

            {!isInitialized && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span className="text-muted">Loading...</span>
              </div>
            )}
          </div>

          {error && (
            <div className="invalid-feedback d-block">
              <i className="bi bi-exclamation-circle me-1"></i>
              {error}
            </div>
          )}

          <div className="mt-2 d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearSignature();
              }}
              className="btn btn-sm btn-outline-secondary"
              disabled={disabled}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>
              Clear
            </button>

            <small className="text-muted">
              {isEmpty ? (
                <>
                  <i className="bi bi-pencil me-1"></i>
                  {disabled ? "Signature required" : "Sign in the box above"}
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle text-success me-1"></i>
                  Signature captured
                </>
              )}
            </small>
          </div>

          {/* Hidden input for form validation */}
          {required && (
            <input
              type="hidden"
              value={value || ""}
              required={required}
              style={{
                position: "absolute",
                opacity: 0,
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      );
    }
  )
);

SignatureField.displayName = "SignatureField";

export default SignatureField;
